import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';

const GOLD = '#d4af37';

interface HeritageItem {
  id: string;
  name: string;
  distance: string;
  type: string;
  location: string;
  description: string;
  imageUrl?: string;
  historicalImportance?: string;
  whyVisit?: string;
  bestTimeToVisit?: string;
}

interface HeritageContent {
  title: string;
  description: string;
  heritages: HeritageItem[];
}

const HeritageNearbyManager = () => {
  const [content, setContent] = useState<HeritageContent>({
    title: 'Heritage Sites Nearby',
    description: 'Historic and cultural heritage sites in the region',
    heritages: [
      { id: '1', name: 'Historic Fort', distance: 'Nearby', type: 'Heritage Site', location: 'Historical Area', description: 'Ancient fort with historical significance', imageUrl: '' },
      { id: '2', name: 'Ancient Monument', distance: '3 km', type: 'Monument', location: 'Cultural Area', description: 'Ancient monument of architectural importance', imageUrl: '' },
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingHeritageId, setUploadingHeritageId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const heritageFileInputRef = useRef<HTMLInputElement>(null);
  const pendingHeritageId = useRef<string | null>(null);
  const heritageFocusHandler = useRef<(() => void) | null>(null);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'nearby_content', 'heritage'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as HeritageContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'nearby_content', 'heritage'), content);
      showSnackbar('Heritage data saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddHeritage = () => {
    const newHeritage: HeritageItem = {
      id: Date.now().toString(),
      name: 'New Heritage Site',
      distance: 'Nearby',
      type: 'Heritage Site',
      location: '',
      description: '',
      imageUrl: '',
      historicalImportance: '',
      whyVisit: '',
      bestTimeToVisit: '',
    };
    setContent({
      ...content,
      heritages: [...content.heritages, newHeritage]
    });
  };

  const handleHeritageChange = (id: string, field: keyof HeritageItem, value: string) => {
    setContent({
      ...content,
      heritages: content.heritages.map(heritage =>
        heritage.id === id ? { ...heritage, [field]: value } : heritage
      )
    });
  };

  const handleDeleteHeritage = (id: string) => {
    setContent({
      ...content,
      heritages: content.heritages.filter(heritage => heritage.id !== id)
    });
  };

  // ─── Auto-compress image to base64 ───
  const compressImage = (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.92): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        let base64 = canvas.toDataURL('image/jpeg', quality);
        let q = quality;
        while (base64.length > 900_000 && q > 0.5) {
          q -= 0.05;
          base64 = canvas.toDataURL('image/jpeg', q);
        }
        if (base64.length > 900_000) {
          const smallCanvas = document.createElement('canvas');
          const scale = 0.75;
          smallCanvas.width = Math.round(width * scale);
          smallCanvas.height = Math.round(height * scale);
          const sCtx = smallCanvas.getContext('2d')!;
          sCtx.imageSmoothingEnabled = true;
          sCtx.imageSmoothingQuality = 'high';
          sCtx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
          base64 = smallCanvas.toDataURL('image/jpeg', 0.8);
        }
        resolve(base64);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  // ─── Heritage Image Upload (auto-compressed) ───
  const handleHeritageFileUpload = async (id: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    setUploadingHeritageId(id);
    setUploadProgress(10);
    try {
      setUploadProgress(30);
      const base64 = await compressImage(file);
      setUploadProgress(90);
      setContent(prev => ({
        ...prev,
        heritages: prev.heritages.map(h =>
          h.id === id ? { ...h, imageUrl: base64 } : h
        )
      }));
      setUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingHeritageId(null);
        setUploadProgress(0);
        showSnackbar(`Image compressed (${originalKB}KB → ${compressedKB}KB) & uploaded! Click "Save Changes" to persist.`, 'success');
      }, 400);
    } catch {
      showSnackbar('Failed to process image', 'error');
      setUploadingHeritageId(null);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Heritage Sites Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage nearby heritage sites content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🏛️ Section Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            📜 Heritage Sites ({content.heritages.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddHeritage}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Site
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.heritages.map((heritage) => (
            <Grid item xs={12} key={heritage.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site Name"
                      value={heritage.name}
                      onChange={(e) => handleHeritageChange(heritage.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Type"
                      value={heritage.type}
                      onChange={(e) => handleHeritageChange(heritage.id, 'type', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Distance"
                      value={heritage.distance}
                      onChange={(e) => handleHeritageChange(heritage.id, 'distance', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteHeritage(heritage.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={heritage.location}
                      onChange={(e) => handleHeritageChange(heritage.id, 'location', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={heritage.description}
                      onChange={(e) => handleHeritageChange(heritage.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Historical Importance (one point per line)"
                      value={heritage.historicalImportance || ''}
                      onChange={(e) => handleHeritageChange(heritage.id, 'historicalImportance', e.target.value)}
                      multiline
                      rows={4}
                      size="small"
                      placeholder={"Also called Iringal Kottakkal Fort\nHeadquarters of Kunjali Marakkar IV\nSymbol of Kerala's naval warfare history"}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Why Visit (one reason per line)"
                      value={heritage.whyVisit || ''}
                      onChange={(e) => handleHeritageChange(heritage.id, 'whyVisit', e.target.value)}
                      multiline
                      rows={3}
                      size="small"
                      placeholder={"Real historical fort (500+ years old)\nKerala maritime history\nFreedom struggle against Portuguese"}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Best Time to Visit (one line per detail)"
                      value={heritage.bestTimeToVisit || ''}
                      onChange={(e) => handleHeritageChange(heritage.id, 'bestTimeToVisit', e.target.value)}
                      multiline
                      rows={3}
                      size="small"
                      placeholder={"Morning: 9 AM \u2013 12 PM\nEvening: 3 PM \u2013 5 PM\nClosed on some Mondays (varies)"}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={heritage.imageUrl?.startsWith('data:') ? '(Uploaded image)' : heritage.imageUrl || ''}
                      onChange={(e) => handleHeritageChange(heritage.id, 'imageUrl', e.target.value)}
                      size="small"
                      disabled={heritage.imageUrl?.startsWith('data:')}
                      placeholder="https://example.com/heritage-image.jpg"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingHeritageId === heritage.id}
                        onClick={() => {
                          pendingHeritageId.current = heritage.id;
                          heritageFileInputRef.current?.click();
                          const onFocus = () => {
                            setTimeout(() => {
                              if (pendingHeritageId.current === heritage.id) {
                                pendingHeritageId.current = null;
                              }
                              setUploadingHeritageId((prev) => prev === heritage.id ? null : prev);
                              setUploadProgress(0);
                            }, 300);
                            window.removeEventListener('focus', onFocus);
                            heritageFocusHandler.current = null;
                          };
                          heritageFocusHandler.current = onFocus;
                          window.addEventListener('focus', onFocus);
                        }}
                        sx={{
                          borderColor: GOLD, color: GOLD, fontWeight: 600, fontSize: '0.72rem',
                          textTransform: 'none', height: 34,
                          '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                        }}
                      >
                        {uploadingHeritageId === heritage.id ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {heritage.imageUrl?.startsWith('data:') && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => handleHeritageChange(heritage.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear & use URL
                        </Button>
                      )}
                    </Box>
                    {uploadingHeritageId === heritage.id && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {heritage.imageUrl && (
                      <Box sx={{ width: '100%', height: 100, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                        <img src={heritage.imageUrl} alt={heritage.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Hidden file input for heritage images */}
      <input
        type="file"
        accept="image/*"
        ref={heritageFileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (heritageFocusHandler.current) {
            window.removeEventListener('focus', heritageFocusHandler.current);
            heritageFocusHandler.current = null;
          }
          const file = e.target.files?.[0];
          if (file && pendingHeritageId.current) {
            handleHeritageFileUpload(pendingHeritageId.current, file);
          }
          pendingHeritageId.current = null;
          e.target.value = '';
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HeritageNearbyManager;
